import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  InputAdornment,
  alpha,
  LinearProgress
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import PrintIcon from "@mui/icons-material/Print";
import PrintReportHeader from "../../components/PrintReportHeader";
import BusinessProfitCalculatorDialog from "../../components/BusinessProfitCalculator";
import BusinessService from "../services/BusinessService";
import { fNumber } from "../../utils/formatNumber";

export default function BusinessOverview() {
  const { user: currentUser } = useSelector((state) => state.auth || {});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [calcOpen, setCalcOpen] = useState(false);

  const getLast30DaysRange = () => {
    const now = new Date();
    const prior = new Date();
    prior.setDate(prior.getDate() - 30);
    const formatDate = (d) => d.toISOString().slice(0, 10);
    return {
      startDate: formatDate(prior),
      endDate: formatDate(now),
    };
  };

  // Calendar filter state (defaults to Last 30 Days)
  const [startDate, setStartDate] = useState(() => getLast30DaysRange().startDate);
  const [endDate, setEndDate] = useState(() => getLast30DaysRange().endDate);
  const [activePreset, setActivePreset] = useState("last30");

  // Tabs for detail views: 0 = Monthly Breakdown, 1 = Yearly Breakdown, 2 = Outstanding Debtors, 3 = Trade Profit Ledger
  const [detailTab, setDetailTab] = useState(0);

  // Debtor search query
  const [debtorSearch, setDebtorSearch] = useState("");

  const fetchSummary = useCallback(async (start, end) => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (start) params.startDate = start;
      if (end) params.endDate = end;
      const res = await BusinessService.getSummary(params);
      setSummaryData(res?.data || null);
    } catch (err) {
      console.error("fetchSummary error:", err);
      setError(err?.response?.data?.message || err?.message || "Failed to load business summary");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary(startDate, endDate);
  }, [fetchSummary, startDate, endDate]);

  const setPresetRange = (preset) => {
    setActivePreset(preset);
    const now = new Date();
    if (preset === "all") {
      setStartDate("");
      setEndDate("");
    } else if (preset === "thisMonth") {
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      setStartDate(`${year}-${month}-01`);
      setEndDate(`${year}-${month}-${String(lastDay).padStart(2, "0")}`);
    } else if (preset === "last30") {
      const prior = new Date();
      prior.setDate(prior.getDate() - 30);
      const formatDate = (d) => d.toISOString().slice(0, 10);
      setStartDate(formatDate(prior));
      setEndDate(formatDate(now));
    } else if (preset === "thisYear") {
      const year = now.getFullYear();
      setStartDate(`${year}-01-01`);
      setEndDate(now.toISOString().slice(0, 10));
    }
  };

  const handleCustomDateChange = (type, val) => {
    setActivePreset("custom");
    if (type === "start") setStartDate(val);
    if (type === "end") setEndDate(val);
  };

  // Filter debtors by search
  const filteredDebtors = useMemo(() => {
    const list = summaryData?.debtors || [];
    if (!debtorSearch.trim()) return list;
    const q = debtorSearch.toLowerCase().trim();
    return list.filter((u) => {
      const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
      const uname = (u.username || "").toLowerCase();
      const phone = (u.phoneNumber || "").toLowerCase();
      return name.includes(q) || uname.includes(q) || phone.includes(q);
    });
  }, [summaryData, debtorSearch]);

  const recoveryRate = useMemo(() => {
    const disbursed = Number(summaryData?.totalDisbursed) || 0;
    const repaid = Number(summaryData?.totalRepayments) || 0;
    if (disbursed <= 0) return 0;
    return Math.min(100, Math.round((repaid / disbursed) * 100));
  }, [summaryData]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Official Print Header (Visible only during printing) */}
      <PrintReportHeader
        title="BUSINESS PORTFOLIO & COMMERCE STATEMENT"
        subtitle={
          detailTab === 0
            ? "Comprehensive monthly analysis of business disbursements, sales, and recoveries"
            : detailTab === 1
            ? "Comprehensive annual trend analysis of business disbursements and recoveries"
            : detailTab === 2
            ? "Complete schedule of active business debtors and outstanding capital exposure"
            : "Official trade sales and realized profit margin ledger"
        }
        period={
          activePreset === "all"
            ? "All Time"
            : startDate && endDate
            ? `${startDate} to ${endDate}`
            : activePreset === "thisMonth"
            ? "This Month"
            : activePreset === "thisYear"
            ? "This Year"
            : activePreset === "last30"
            ? "Last 30 Days"
            : "Current Filter Period"
        }
        officer={
          currentUser
            ? `${currentUser.firstName || ''} ${currentUser.lastName || ''} (${currentUser.username || 'Officer'})`.trim()
            : "Business Officer"
        }
        metrics={[
          { label: "Total Outstanding Business", value: `₦${fNumber(summaryData?.totalOutstandingBusinessBalance || 0)}`, color: "#DC2626" },
          { label: "Total Disbursed", value: `₦${fNumber(summaryData?.totalDisbursed || 0)}`, color: "#0B3B60" },
          { label: "Total Recovered", value: `₦${fNumber(summaryData?.totalRepayments || 0)}`, color: "#059669" },
          { label: "Recovery Rate", value: `${recoveryRate}%`, color: recoveryRate >= 75 ? "#059669" : "#D97706" }
        ]}
      />

      {/* Header Banner */}
      <Box
        className="no-print"
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          background: "linear-gradient(135deg, #0B3B60 0%, #0D9488 100%)",
          color: "#ffffff",
          boxShadow: "0 10px 25px -5px rgba(11, 59, 96, 0.25)",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(4px)",
              }}
            >
              <StorefrontIcon sx={{ color: "#ffffff", fontSize: 26 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
              Business Portfolio &amp; Capital Overview
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.85)", ml: 6.5 }}>
            Real-time analytics on business outstanding debt, purchases, repayments, and period trends.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{
              backgroundColor: "#ffffff",
              color: "#0B3B60",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              "&:hover": { backgroundColor: "#F8FAFC" },
            }}
          >
            Print Statement
          </Button>
          <Button
            variant="contained"
            onClick={() => setCalcOpen(true)}
            startIcon={<CalculateRoundedIcon />}
            sx={{
              backgroundColor: "#F59E0B",
              color: "#ffffff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "0 2px 6px rgba(245, 158, 11, 0.35)",
              "&:hover": { backgroundColor: "#D97706" },
            }}
          >
            Margin Calculator
          </Button>
          <Button
            variant="contained"
            component={Link}
            to="/dashboard/financials"
            startIcon={<ArrowForwardIcon />}
            sx={{
              backgroundColor: "#ffffff",
              color: "#0B3B60",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              "&:hover": { backgroundColor: "#F8FAFC" },
            }}
          >
            Manage Accounts
          </Button>
          <Button
            variant="outlined"
            onClick={() => fetchSummary(startDate, endDate)}
            sx={{
              color: "#ffffff",
              borderColor: "rgba(255,255,255,0.6)",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              "&:hover": { borderColor: "#ffffff", backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Calendar Date Range & Presets Filter */}
      <Paper
        className="no-print"
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          border: "1px solid #E2E8F0",
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          {/* Custom Date Pickers */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CalendarMonthIcon sx={{ color: "#0D9488", fontSize: 22 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                Filter Period:
              </Typography>
            </Box>

            <TextField
              type="date"
              size="small"
              label="Start Date"
              value={startDate}
              onChange={(e) => handleCustomDateChange("start", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, backgroundColor: "#F8FAFC" }}
            />
            <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 700 }}>
              to
            </Typography>
            <TextField
              type="date"
              size="small"
              label="End Date"
              value={endDate}
              onChange={(e) => handleCustomDateChange("end", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, backgroundColor: "#F8FAFC" }}
            />

            {(startDate || endDate) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPresetRange("all")}
                sx={{
                  borderRadius: "8px",
                  borderColor: "#EF4444",
                  color: "#DC2626",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#DC2626", backgroundColor: "rgba(239, 68, 68, 0.04)" },
                }}
              >
                Clear Dates
              </Button>
            )}
          </Box>

          {/* Quick Presets */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>
              Presets:
            </Typography>
            {[
              { key: "last30", label: "Last 30 Days" },
              { key: "thisMonth", label: "This Month" },
              { key: "thisYear", label: "This Year" },
              { key: "all", label: "All Time" },
            ].map(({ key, label }) => (
              <Button
                key={key}
                size="small"
                variant={activePreset === key ? "contained" : "outlined"}
                onClick={() => setPresetRange(key)}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: activePreset === key ? "#0D9488" : "#ffffff",
                  borderColor: activePreset === key ? "#0D9488" : "#CBD5E1",
                  color: activePreset === key ? "#ffffff" : "#334155",
                  boxShadow: activePreset === key ? "0 2px 6px rgba(13, 148, 136, 0.25)" : "none",
                  "&:hover": {
                    backgroundColor: activePreset === key ? "#0F766E" : "rgba(13, 148, 136, 0.06)",
                    borderColor: "#0F766E",
                  },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Loading & Error States */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#0D9488" }} />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3, border: "1px solid #FECACA", backgroundColor: "#FEF2F2" }}>
          <Typography color="error" sx={{ fontWeight: 600 }}>{error}</Typography>
        </Paper>
      ) : (
        <>
          {/* DEDICATED TRADE PROFIT MARGIN ACCOUNT */}
          <Paper
            className="no-print"
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: "linear-gradient(135deg, #064E3B 0%, #065F46 45%, #047857 100%)",
              color: "#ffffff",
              boxShadow: "0 10px 25px -5px rgba(6, 78, 59, 0.3)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -30,
                right: -30,
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)",
                pointerEvents: "none",
              }}
            />

            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    backgroundColor: "rgba(255, 255, 255, 0.18)",
                    backdropFilter: "blur(4px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PaidRoundedIcon sx={{ color: "#34D399", fontSize: 28 }} />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.01em" }}>
                      Dedicated Trade Profit Margin Account
                    </Typography>
                    <Chip
                      label="MTC-ACC-PROFIT-POOL"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(52, 211, 153, 0.2)",
                        color: "#A7F3D0",
                        fontWeight: 800,
                        fontSize: "0.7rem",
                        border: "1px solid rgba(52, 211, 153, 0.4)",
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.82)" }}>
                    Designated ledger account capturing automated markup margins (5% for 3m, 10% for 6m, 12% for 9m &amp; custom rates)
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setDetailTab(3)}
                  sx={{
                    backgroundColor: "#ffffff",
                    color: "#065F46",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "#F0FDF4" },
                  }}
                >
                  View Profit Ledger ({summaryData?.profitMarginAccount?.profitTransactions?.length || 0})
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setCalcOpen(true)}
                  startIcon={<CalculateRoundedIcon />}
                  sx={{
                    borderColor: "rgba(255, 255, 255, 0.5)",
                    color: "#ffffff",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "8px",
                    "&:hover": { borderColor: "#ffffff", backgroundColor: "rgba(255, 255, 255, 0.1)" },
                  }}
                >
                  Quick Calculator
                </Button>
              </Box>
            </Box>

            {/* 4 Dedicated Account Metrics */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.75)", fontWeight: 700, textTransform: "uppercase" }}>
                    Accrued Trade Profit
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#34D399", mt: 0.5 }}>
                    ₦{fNumber(summaryData?.profitMarginAccount?.totalProfitMarginAccrued || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    Cumulative trade markups earned
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.75)", fontWeight: 700, textTransform: "uppercase" }}>
                    Capital Cost Deployed
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#ffffff", mt: 0.5 }}>
                    ₦{fNumber(summaryData?.profitMarginAccount?.totalCostOfGoods || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    Base wholesale purchasing cost
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.75)", fontWeight: 700, textTransform: "uppercase" }}>
                    Total Billed Receivables
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#FDE68A", mt: 0.5 }}>
                    ₦{fNumber(summaryData?.profitMarginAccount?.totalDisbursedWithMargin || 0)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    Total cost + profit billed to buyers
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.75)", fontWeight: 700, textTransform: "uppercase" }}>
                    Portfolio Margin Yield
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#6EE7B7", mt: 0.5 }}>
                    {summaryData?.profitMarginAccount?.overallMarginRate || 0}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
                    Average effective margin across sales
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Tenor Accrual Breakdown Pills */}
            <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid rgba(255, 255, 255, 0.15)", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.8)", fontWeight: 700 }}>
                Tenor Performance Breakdown:
              </Typography>
              <Chip
                label={`3 Months (5% Std): ${summaryData?.profitMarginAccount?.tenorCounts?.['3'] || 0} trades • ₦${fNumber(summaryData?.profitMarginAccount?.tenorProfits?.['3'] || 0)} profit`}
                size="small"
                sx={{ backgroundColor: "rgba(255, 255, 255, 0.15)", color: "#ffffff", fontWeight: 600, fontSize: "0.74rem" }}
              />
              <Chip
                label={`6 Months (10% Std): ${summaryData?.profitMarginAccount?.tenorCounts?.['6'] || 0} trades • ₦${fNumber(summaryData?.profitMarginAccount?.tenorProfits?.['6'] || 0)} profit`}
                size="small"
                sx={{ backgroundColor: "rgba(255, 255, 255, 0.15)", color: "#ffffff", fontWeight: 600, fontSize: "0.74rem" }}
              />
              <Chip
                label={`9 Months (12% Std): ${summaryData?.profitMarginAccount?.tenorCounts?.['9'] || 0} trades • ₦${fNumber(summaryData?.profitMarginAccount?.tenorProfits?.['9'] || 0)} profit`}
                size="small"
                sx={{ backgroundColor: "rgba(255, 255, 255, 0.15)", color: "#ffffff", fontWeight: 600, fontSize: "0.74rem" }}
              />
            </Box>
          </Paper>

          {/* Top KPI Metric Cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }} className="no-print">
            {/* Card 1: Outstanding Balance */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#DC2626",
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                    Total Outstanding Debt
                  </Typography>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: "rgba(220, 38, 38, 0.08)",
                      color: "#DC2626",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccountBalanceWalletIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#DC2626", mb: 1 }}>
                  ₦{fNumber(summaryData?.totalOutstandingBalance || 0)}
                </Typography>
                <Chip
                  label={`${summaryData?.activeDebtorsCount || 0} Members With Unpaid Balance`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    backgroundColor: "rgba(220, 38, 38, 0.08)",
                    color: "#DC2626",
                    border: "1px solid rgba(220, 38, 38, 0.2)",
                  }}
                />
              </Paper>
            </Grid>

            {/* Card 2: Total Capital Disbursed / Buy */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#0B3B60",
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                    Total Capital Disbursed (Buy)
                  </Typography>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: alpha("#0B3B60", 0.08),
                      color: "#0B3B60",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TrendingUpIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0B3B60", mb: 1 }}>
                  ₦{fNumber(summaryData?.totalDisbursed || 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600 }}>
                  Purchases made by members on credit
                </Typography>
              </Paper>
            </Grid>

            {/* Card 3: Total Repayments Recovered */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#059669",
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                    Capital Recovered (Repaid)
                  </Typography>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: "rgba(5, 150, 105, 0.08)",
                      color: "#059669",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TrendingDownIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#059669", mb: 1 }}>
                  ₦{fNumber(summaryData?.totalRepayments || 0)}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={recoveryRate}
                    sx={{
                      flexGrow: 1,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "#E2E8F0",
                      "& .MuiLinearProgress-bar": { backgroundColor: "#059669" },
                    }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#059669" }}>
                    {recoveryRate}%
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Card 4: Period Net & Transactions */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#ffffff",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    backgroundColor: "#0D9488",
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                    Period Net Capital
                  </Typography>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: "rgba(13, 148, 136, 0.08)",
                      color: "#0D9488",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <StorefrontIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#0D9488", mb: 1 }}>
                  ₦{fNumber(summaryData?.netOutstandingPeriod || 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600 }}>
                  {summaryData?.transactionCount || 0} Total Transactions Recorded
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Details Section with Tabs: Monthly Breakdown, Yearly Breakdown, Outstanding Debtors */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              backgroundColor: "#ffffff",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
            }}
          >
            {/* Section Header for Printouts */}
            <Box className="print-only" sx={{ display: 'none', px: 2.5, py: 1.5, borderBottom: '1px solid #CBD5E1' }}>
              <Typography sx={{ fontSize: '11pt', fontWeight: 800, color: '#0B3B60', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                {detailTab === 0
                  ? "Monthly Business Commerce Breakdown"
                  : detailTab === 1
                  ? "Yearly Business Commerce Breakdown"
                  : detailTab === 2
                  ? "Outstanding Business Debtors Schedule"
                  : "Trade Profit & Markup Ledger"}
              </Typography>
            </Box>

            <Box
              className="no-print"
              sx={{
                px: 3,
                pt: 2,
                borderBottom: "1px solid #E2E8F0",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                backgroundColor: "#F8FAFC",
              }}
            >
              <Tabs
                value={detailTab}
                onChange={(e, val) => setDetailTab(val)}
                textColor="primary"
                indicatorColor="primary"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    minHeight: 48,
                    color: "#64748B",
                    "&.Mui-selected": { color: "#0B3B60" },
                  },
                  "& .MuiTabs-indicator": { backgroundColor: "#0B3B60", height: 3 },
                }}
              >
                <Tab label={`Monthly Breakdown (${summaryData?.monthlyBreakdown?.length || 0})`} />
                <Tab label={`Yearly Breakdown (${summaryData?.yearlyBreakdown?.length || 0})`} />
                <Tab label={`Outstanding Debtors (${summaryData?.debtors?.length || 0})`} />
                <Tab label={`Trade Profit Ledger (${summaryData?.profitMarginAccount?.profitTransactions?.length || 0})`} />
              </Tabs>

              {detailTab === 2 && (
                <TextField
                  size="small"
                  placeholder="Search debtor name/phone..."
                  value={debtorSearch}
                  onChange={(e) => setDebtorSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "#94A3B8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 250,
                    mb: 1,
                    backgroundColor: "#ffffff",
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  }}
                />
              )}
            </Box>

            {/* TAB 0: Monthly Breakdown Table */}
            {detailTab === 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Month</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>
                        Disbursed (Buy)
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#059669", py: 1.5 }}>
                        Repayments (Debit)
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#0D9488", py: 1.5 }}>
                        Net Difference
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>
                        Transactions
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>
                        Recovery Ratio
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(summaryData?.monthlyBreakdown || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#64748B" }}>
                          No monthly transactions found for the selected period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      summaryData.monthlyBreakdown.map((row) => {
                        const mRatio = row.credit > 0 ? Math.min(100, Math.round((row.debit / row.credit) * 100)) : 100;
                        return (
                          <TableRow key={row.month} hover sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }}>
                            <TableCell sx={{ fontWeight: 700, color: "#0F172A", py: 1.5 }}>
                              {row.month}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "#0B3B60" }}>
                              ₦{fNumber(row.credit)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "#059669" }}>
                              ₦{fNumber(row.debit)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: row.net > 0 ? "#D97706" : "#059669" }}>
                              ₦{fNumber(row.net)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={row.count} size="small" sx={{ fontWeight: 600, fontSize: "0.75rem" }} />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={mRatio}
                                  sx={{
                                    width: 80,
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: "#E2E8F0",
                                    "& .MuiLinearProgress-bar": { backgroundColor: mRatio >= 80 ? "#059669" : "#D97706" },
                                  }}
                                />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155" }}>
                                  {mRatio}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* TAB 1: Yearly Breakdown Table */}
            {detailTab === 1 && (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Year</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>
                        Total Capital Disbursed (Buy)
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#059669", py: 1.5 }}>
                        Total Repayments Collected
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#0D9488", py: 1.5 }}>
                        Net Outstanding for Year
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>
                        Transactions
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>
                        Annual Collection %
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(summaryData?.yearlyBreakdown || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#64748B" }}>
                          No yearly transactions found for the selected period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      summaryData.yearlyBreakdown.map((row) => {
                        const yRatio = row.credit > 0 ? Math.min(100, Math.round((row.debit / row.credit) * 100)) : 100;
                        return (
                          <TableRow key={row.year} hover sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }}>
                            <TableCell sx={{ fontWeight: 800, color: "#0B3B60", py: 1.75, fontSize: "0.95rem" }}>
                              {row.year}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "#0B3B60" }}>
                              ₦{fNumber(row.credit)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "#059669" }}>
                              ₦{fNumber(row.debit)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: row.net > 0 ? "#DC2626" : "#059669" }}>
                              ₦{fNumber(row.net)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={row.count} size="small" sx={{ fontWeight: 600, fontSize: "0.75rem" }} />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={yRatio}
                                  sx={{
                                    width: 100,
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: "#E2E8F0",
                                    "& .MuiLinearProgress-bar": { backgroundColor: yRatio >= 75 ? "#059669" : "#D97706" },
                                  }}
                                />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155" }}>
                                  {yRatio}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* TAB 2: Outstanding Debtors Table */}
            {detailTab === 2 && (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Member Name</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Username</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Phone Number</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#DC2626", py: 1.5 }}>
                        Outstanding Business Debt
                      </TableCell>
                      <TableCell align="center" className="print-hide-action" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDebtors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#64748B" }}>
                          No members with outstanding business debt found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDebtors.map((u) => {
                        const fullName = `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username;
                        return (
                          <TableRow key={u._id} hover sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }}>
                            <TableCell sx={{ fontWeight: 700, color: "#0F172A", py: 1.5 }}>
                              {fullName}
                            </TableCell>
                            <TableCell sx={{ color: "#64748B", fontSize: "0.85rem" }}>
                              @{u.username}
                            </TableCell>
                            <TableCell sx={{ color: "#334155", fontSize: "0.85rem" }}>
                              {u.phoneNumber || "—"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: "#DC2626", fontSize: "0.95rem" }}>
                              ₦{fNumber(u.businessBalance || 0)}
                            </TableCell>
                            <TableCell align="center" className="print-hide-action">
                              <Button
                                size="small"
                                variant="outlined"
                                component={Link}
                                to={`/dashboard/business/${u._id}`}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  borderRadius: "6px",
                                  borderColor: "#0D9488",
                                  color: "#0D9488",
                                  "&:hover": { backgroundColor: "rgba(13, 148, 136, 0.08)", borderColor: "#0F766E" },
                                }}
                              >
                                View History
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* TAB 3: Dedicated Trade Profit Margin Ledger */}
            {detailTab === 3 && (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Buyer / Member</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Item / Details</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Tenor</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Margin %</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Base Cost (₦)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#059669", py: 1.5 }}>Accrued Profit (₦)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60", py: 1.5 }}>Total Billed (₦)</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Tenor Status</TableCell>
                      <TableCell align="center" className="print-hide-action" sx={{ fontWeight: 800, color: "#334155", py: 1.5 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!summaryData?.profitMarginAccount?.profitTransactions || summaryData.profitMarginAccount.profitTransactions.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 5, color: "#64748B" }}>
                          No trade profit margin transactions found in selected period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      summaryData.profitMarginAccount.profitTransactions.map((tx) => {
                        const member = tx.owner;
                        const memberName = member
                          ? `${member.firstName || ""} ${member.lastName || ""}`.trim() || member.username
                          : "Unknown Member";
                        const memberId = member?._id || member;

                        return (
                          <TableRow key={tx._id} hover sx={{ "&:hover": { backgroundColor: "#F8FAFC" } }}>
                            <TableCell sx={{ color: "#475569", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                              {tx.date ? new Date(tx.date).toLocaleDateString() : "—"}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: "#0F172A" }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {memberName}
                              </Typography>
                              {member?.username && (
                                <Typography variant="caption" sx={{ color: "#64748B" }}>
                                  @{member.username}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ color: "#334155", fontSize: "0.85rem", maxWidth: 220 }}>
                              {tx.details || "Commodity / Item Purchase"}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${tx.tenor || "—"} mo`}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  backgroundColor: tx.tenor === 3 ? "rgba(13, 148, 136, 0.1)" : tx.tenor === 6 ? "rgba(37, 99, 235, 0.1)" : "rgba(124, 58, 237, 0.1)",
                                  color: tx.tenor === 3 ? "#0D9488" : tx.tenor === 6 ? "#2563EB" : "#7C3AED",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${tx.profitMarginRate ?? 0}%`}
                                size="small"
                                sx={{
                                  fontWeight: 800,
                                  backgroundColor: "rgba(5, 150, 105, 0.1)",
                                  color: "#059669",
                                }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: "#475569" }}>
                              ₦{fNumber(tx.costAmount || 0)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: "#059669" }}>
                              +₦{fNumber(tx.profitMarginAmount || 0)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                              ₦{fNumber(tx.totalAmount || 0)}
                            </TableCell>
                            <TableCell align="center">
                              {tx.tenorRestructured ? (
                                <Chip
                                  label={`Restructured (${tx.tenorHistoryCount}x)`}
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.7rem",
                                    backgroundColor: "rgba(245, 158, 11, 0.15)",
                                    color: "#B45309",
                                    border: "1px solid rgba(245, 158, 11, 0.3)",
                                  }}
                                />
                              ) : (
                                <Chip
                                  label="Original Tenor"
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                    backgroundColor: "#F1F5F9",
                                    color: "#64748B",
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="center" className="print-hide-action">
                              <Button
                                size="small"
                                variant="outlined"
                                component={Link}
                                to={`/dashboard/business/${memberId}`}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  borderRadius: "6px",
                                  borderColor: "#0B3B60",
                                  color: "#0B3B60",
                                  "&:hover": { backgroundColor: "rgba(11, 59, 96, 0.08)", borderColor: "#0B3B60" },
                                }}
                              >
                                View / Change Tenor
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {/* Reusable Business Profit Margin Calculator Dialog */}
      <BusinessProfitCalculatorDialog
        open={calcOpen}
        onClose={() => setCalcOpen(false)}
      />
    </Container>
  );
}
